<!--
// nuxt-ui/components/pages/teams-team/CurrentSeasonTableDataTeamPage.vue
-->
<script setup lang="ts">
import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import type { SeasonCluster } from "~/src/season-cluster/domain/models/00.SeasonCluster";
import type { UnwrapRefSimple } from "@vue/reactivity";
import { useTeams } from "~/nuxt-ui/Shared/composables/teams";
import { assignPositionOrdinalAbbreviation } from "~/src/team/domain/services/teamService";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";

type Props = {
  teamId: number;
  currentSeason: Season;
  currentSeasonCluster: UnwrapRefSimple<SeasonCluster> | undefined;
};

const props = defineProps<Props>();

const { getTeamGroupId, getTeamStanding, getSeasonTeamRankings } = useTeams();
const { locale } = useI18n();
const { currentSeasonCluster, currentSeasonTeams } = useCompetitionStore();

const teamGroupId = await getTeamGroupId(props.currentSeason.id, props.teamId);
const seasonIsWorldCup = props.currentSeason.id === 13 || props.currentSeason.id === 28;

const teamBudget = seasonIsWorldCup
  ? undefined
  : currentSeasonTeams.length && currentSeasonTeams.find(t => t.id === props.teamId)
    ? currentSeasonTeams.find(t => t.id === props.teamId).budget
    : undefined;

const setSeasonClusterName = async () => {
  if (currentSeasonCluster && props.currentSeasonCluster === currentSeasonCluster.id) {
    return currentSeasonCluster.name;
  } else {
    const { data } = await useFetch("/api/v1/competition/season-clusters/available");
    const seasonClustersAvailable = data.value || [];
    return seasonClustersAvailable.find(sc => sc.id === props.currentSeasonCluster).name || "";
  }
};

const teamCurrentSeasonCluster = await setSeasonClusterName();

const teamGroupStanding = teamGroupId ? await getTeamStanding(teamGroupId, props.teamId) : undefined;
const teamSeasonRankings = await getSeasonTeamRankings(props.currentSeason.id, props.teamId);

const teamStandingData = reactive({
  rank: teamGroupStanding?.rank ? teamGroupStanding?.rank : undefined,
  gameWon: teamGroupStanding?.gameWon
    ? teamGroupStanding?.gameWon
    : teamSeasonRankings
      ? teamSeasonRankings.find(r => r.parameter.code === "WIN")?.total
      : undefined,
  gamePenaltyWon: teamGroupStanding?.gamePenaltyWon ? teamGroupStanding?.gamePenaltyWon : undefined,
  gamePenaltyLost: teamGroupStanding?.gamePenaltyLost ? teamGroupStanding?.gamePenaltyLost : undefined,
  gameLost: teamGroupStanding?.gameLost
    ? teamGroupStanding?.gameLost
    : teamSeasonRankings
      ? teamSeasonRankings.find(r => r.parameter.code === "LOSE")?.total
      : undefined,
  goalPro: teamGroupStanding?.goalPro
    ? teamGroupStanding?.goalPro
    : teamSeasonRankings
      ? teamSeasonRankings.find(r => r.parameter.code === "GOL")?.total
      : undefined,
  goalAgainst: teamGroupStanding?.goalAgainst
    ? teamGroupStanding?.goalAgainst
    : teamSeasonRankings
      ? teamSeasonRankings.find(r => r.parameter.code === "GOL-S")?.total
      : undefined,
  goalDifference:
    teamGroupStanding?.goalPro && teamGroupStanding?.goalAgainst
      ? Number(teamGroupStanding?.goalPro) - Number(teamGroupStanding?.goalAgainst)
      : teamSeasonRankings &&
          teamSeasonRankings.find(r => r.parameter.code === "GOL") &&
          teamSeasonRankings.find(r => r.parameter.code === "GOL-S")
        ? Number(teamSeasonRankings.find(r => r.parameter.code === "GOL")?.total) -
          Number(teamSeasonRankings.find(r => r.parameter.code === "GOL-S")?.total)
        : undefined,
  yellowCards: teamSeasonRankings
    ? teamSeasonRankings.find(r => r.parameter.code === "CRT-G")?.total
    : undefined,
  redCards: teamSeasonRankings
    ? teamSeasonRankings.find(r => r.parameter.code === "CRT-R")?.total
    : undefined,
});
</script>

<template>
  <div class="bg-gray-800 rounded-lg py-6 px-4 md:py-12 md:px-10 md:text-center">
    <p
      v-if="currentSeasonCluster && currentSeason"
      class="text-sm flex gap-x-1 md:gap-x-10 items-center md:justify-center flex-wrap"
    >
      <span>{{ teamCurrentSeasonCluster }} - {{ currentSeason ? currentSeason?.displayName : "" }}</span>
      <span v-if="teamBudget">|</span>
      <span v-if="teamBudget">
        {{ $t("team.budget.title") }} <span class="font-bold">{{ teamBudget }} M</span>
      </span>
    </p>
    <p v-else>-</p>

    <div class="md:flex md:justify-evenly md:items-center pt-4 md:py-6">
      <div v-if="!seasonIsWorldCup" class="item-standing-team">
        <p class="item-value-number">
          {{ teamStandingData.rank ? teamStandingData.rank : "-"
          }}<span v-if="locale === 'de'">{{
            assignPositionOrdinalAbbreviation(teamStandingData.rank, locale)
          }}</span
          ><sup v-else>{{ assignPositionOrdinalAbbreviation(teamStandingData.rank, locale) }}</sup>
        </p>
        <p class="item-description-text">{{ $t("team.position.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">{{ teamStandingData.gameWon ? teamStandingData.gameWon : 0 }}</p>
        <p class="item-description-text">{{ $t("team.wins.standing.title") }}</p>
      </div>
      <div v-if="!seasonIsWorldCup" class="item-standing-team">
        <p class="item-value-number">
          {{ teamStandingData.gamePenaltyWon ? teamStandingData.gamePenaltyWon : 0 }}
        </p>
        <p class="item-description-text">{{ $t("team.pWins.standing.title") }}</p>
      </div>
      <div v-if="!seasonIsWorldCup" class="item-standing-team">
        <p class="item-value-number">
          {{ teamStandingData.gamePenaltyLost ? teamStandingData.gamePenaltyLost : 0 }}
        </p>
        <p class="item-description-text">{{ $t("team.pLoses.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">{{ teamStandingData.gameLost ? teamStandingData.gameLost : 0 }}</p>
        <p class="item-description-text">{{ $t("team.loses.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">{{ teamStandingData.goalPro ? teamStandingData.goalPro : 0 }}</p>
        <p class="item-description-text">{{ $t("team.goalsPro.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">
          {{ teamStandingData.goalAgainst ? teamStandingData.goalAgainst : 0 }}
        </p>
        <p class="item-description-text">{{ $t("team.goalsAgainst.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">
          {{ teamStandingData.goalDifference ? teamStandingData.goalDifference : 0 }}
        </p>
        <p class="item-description-text">{{ $t("team.goalDifference.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">
          {{ teamStandingData.yellowCards ? teamStandingData.yellowCards : 0 }}
        </p>
        <p class="item-description-text">{{ $t("team.yellowCards.standing.title") }}</p>
      </div>
      <div class="item-standing-team">
        <p class="item-value-number">{{ teamStandingData.redCards ? teamStandingData.redCards : 0 }}</p>
        <p class="item-description-text">{{ $t("team.redCards.standing.title") }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.item-standing-team {
  @apply min-h-full flex justify-between md:flex-col md:justify-end items-center gap-1 py-1 md:py-0 border-b border-b-gray-600 md:border-none;
}

.item-value-number {
  @apply font-bold font-['Archivo_Expanded'] text-2xl order-2 md:order-1 md:text-4xl lg:text-7xl;
}

.item-description-text {
  @apply text-primary-light text-sm order-1 lg:order-2;
}
</style>
